import { App as AntdApp, ConfigProvider } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { bindMessageApi } from './utils/antd-message';
import { initOTel } from './observability/otel';
import { startMockWorker } from './api/mocks/browser';
import { adminTheme } from './theme';
import 'antd/dist/reset.css';
import './styles/tokens.css';
import './styles/global.css';

// RUM 采集初始化（document-load + fetch/xhr + React Router span，不开 user-interaction；上报失败静默降级）
initOTel();

// dayjs 全局中文 locale：AntD v5 DatePicker 日历面板的星期/月份文案取自 dayjs 而非 ConfigProvider，
// 必须在应用入口设置一次，日历控件才能完整中文化（与 ConfigProvider zhCN 配合）。
dayjs.locale('zh-cn');

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('根挂载节点 #root 不存在');

// 开发环境：VITE_USE_MOCK !== 'false'（默认启用）时启动 MSW worker 拦截 /api 返回桩数据。
// 设 VITE_USE_MOCK=false 关闭 MSW，经 vite proxy 连真实后端（需 server 已落地对应端点）。
// 生产构建不打包 MSW（import.meta.env.DEV 守卫）；测试环境用 setupServer（src/test/setup.ts）。
// AntdApp 包裹提供 message/modal/notification 的动态主题上下文（AntD 5 静态方法无法消费 ConfigProvider 主题）。
// message/notification 动态实例绑定：AntdApp 内经 App.useApp() 取实例后全局桥接生效
//（antd v5 静态方法无法消费动态主题，全局调用统一走 utils/antd-message 桥接）。
function AntdMessageBinder() {
  const { message, notification } = AntdApp.useApp();
  React.useEffect(() => {
    bindMessageApi(message, notification);
  }, [message, notification]);
  return null;
}

async function bootstrap(): Promise<void> {
  const useMock = import.meta.env.VITE_USE_MOCK !== 'false';
  if (import.meta.env.DEV && useMock) {
    await startMockWorker();
  }
  createRoot(rootEl!).render(
    <React.StrictMode>
      <ConfigProvider locale={zhCN} theme={adminTheme}>
        <AntdApp>
          <AntdMessageBinder />
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </AntdApp>
      </ConfigProvider>
    </React.StrictMode>,
  );
}

bootstrap();
